import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ApplicantShell from "../components/ApplicantShell";
import LatestJobsPreviewPills from "../components/LatestJobsPreviewPills";
import { getApplicantJobSearchLatestPreview, isApiConfigured } from "../apiClient";
import { jobPublicHref } from "../lib/jobPublicPath";

function ApplicantJobsListPage({ currentUser, setCurrentUser, authToken }) {
  const { listType } = useParams();
  const isSavedList = listType === "saved";

  const pageTitle = isSavedList ? "Saved jobs" : "Latest jobs for you";

  const [latestPreview, setLatestPreview] = useState(null);
  const [latestPreviewLoading, setLatestPreviewLoading] = useState(false);

  useEffect(() => {
    if (isSavedList) {
      setLatestPreview(null);
      setLatestPreviewLoading(false);
      return;
    }
    const t = String(authToken || "").trim();
    if (currentUser?.role !== "applicant" || !isApiConfigured() || !t) {
      setLatestPreview(null);
      setLatestPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setLatestPreviewLoading(true);
    (async () => {
      try {
        const data = await getApplicantJobSearchLatestPreview(t);
        if (!cancelled) setLatestPreview(data);
      } catch {
        if (!cancelled) setLatestPreview(null);
      } finally {
        if (!cancelled) setLatestPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser?.role, isSavedList]);

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main
        className={
          currentUser?.role === "applicant"
            ? "jw-applicant-main applicant-home jw-applicant-jobs-list-page"
            : "applicant-home"
        }
      >
        <section className="home-section">
          <Link className="back-link" to={currentUser ? "/applicant-home" : "/"}>
            &larr; Back
          </Link>
          <h1>{pageTitle}</h1>
          {isSavedList ? (
            <>
              <p className="subtitle">
                Save roles from live job search results. There is no static list here.
              </p>
              <p className="saved-search-meta">
                <Link to="/job-search">Open job search</Link>
                {currentUser ? "" : " — sign in as an applicant to query live listings."}
              </p>
            </>
          ) : latestPreviewLoading ? (
            <p className="saved-search-meta" aria-live="polite">
              Loading…
            </p>
          ) : !latestPreview?.hasLastSearch ? (
            <>
              <p className="subtitle">
                Run a job search once and we&apos;ll list up to five roles that match your last criteria.
              </p>
              <p className="saved-search-meta">
                <Link to="/job-search">Open job search</Link>
                {currentUser?.role !== "applicant" ? " — sign in as an applicant." : ""}
              </p>
            </>
          ) : latestPreview.replayFailed ? (
            <>
              <p className="subtitle">We couldn&apos;t refresh results from your last search.</p>
              <p className="saved-search-meta">
                <Link to="/job-search">Try job search again</Link>
              </p>
            </>
          ) : latestPreview.jobs.length === 0 ? (
            <p className="saved-search-meta latest-jobs-preview-empty-copy">
              oops, we could not find any new roles for you. Try{" "}
              <Link to="/job-search" className="latest-jobs-preview-empty-link">
                searching here
              </Link>
            </p>
          ) : (
            <>
              <p className="subtitle">Based on your most recent job search (up to five roles).</p>
              <ul className="latest-jobs-preview-list">
                {latestPreview.jobs.map((job) => (
                  <li key={job.id}>
                    <Link to={jobPublicHref(job)} className="latest-jobs-preview-card">
                      <span className="latest-jobs-preview-title">{job.title || "Role"}</span>
                      {job.company?.trim() ? (
                        <span className="latest-jobs-preview-company">{job.company.trim()}</span>
                      ) : null}
                      <LatestJobsPreviewPills job={job} />
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="saved-search-meta">
                <Link to="/job-search">Open job search</Link>
              </p>
            </>
          )}
        </section>
      </main>
    </ApplicantShell>
  );
}

export default ApplicantJobsListPage;
